"use client"

import { useState, useRef } from "react"
import { createSupportQueryApi } from "../../services/supportService"
import { FiMail, FiMessageSquare, FiCheckCircle, FiPaperclip, FiX } from "react-icons/fi"
import "./ContactForm.css"

export default function ContactForm() {
  const [formData, setFormData] = useState({
    issueType: "bug",
    description: "",
  })
  const [selectedFile, setSelectedFile] = useState(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const fileInputRef = useRef(null)

  const ISSUE_TYPE_LABELS = {
    bug: "Bug Report",
    feature: "Feature Request",
    feedback: "General Feedback",
    suggestion: "Suggestion",
    other: "Other",
    }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // <CHANGE> Added file input handler
  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  // <CHANGE> Added file removal handler
  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  // <CHANGE> Added drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.classList.add('mm-cf-attachment-drag-active')
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.classList.remove('mm-cf-attachment-drag-active')
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.classList.remove('mm-cf-attachment-drag-active')
    const file = e.dataTransfer.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.description.trim()) {
        alert("Please provide a complete description")
        return
    }

    setIsLoading(true)

    try {
        const payload = new FormData()

        payload.append(
          "issueType",
          ISSUE_TYPE_LABELS[formData.issueType] || formData.issueType
        )
        payload.append("description", formData.description.trim())

        if (selectedFile) {
          // "attachment" is the field name your backend should read
          payload.append("files", selectedFile)
        }

        const response = await createSupportQueryApi(payload)

        if (response?.code === 200) {
        // success UI
        setIsSubmitted(true)

        // reset form after a short delay (same behaviour you already had)
        setTimeout(() => {
            setFormData({ issueType: "bug", description: "" })
            setSelectedFile(null)
            setIsSubmitted(false)
        }, 3000)
        } else {
        alert(response?.message || "Something went wrong while submitting the query")
        }
    } catch (error) {
        console.error("Error submitting support query:", error)
        alert("Failed to send your message. Please try again.")
    } finally {
        setIsLoading(false)
    }
    }

  return (
    <div className="mm-cf-form-container">
      <div className="mm-cf-form-wrapper">
        {/* Logo Section */}
        <div className="mm-cf-logo-section">
          <div className="mm-cf-logo-circle">
            <FiMail className="mm-cf-logo-icon" />
          </div>
        </div>

        {/* Header */}
        <h1 className="mm-cf-form-heading">Reach Out to Team Mayamaya</h1>
        <p className="mm-cf-form-subtitle">
          We'd love to hear from you. Please share any issues, suggestions, or feedback you have. Your input helps us
          improve Mayamaya.
        </p>

        {/* Success Message */}
        {isSubmitted && (
          <div className="mm-cf-success-message">
            <FiCheckCircle className="mm-cf-success-icon" />
            <p>Thank you! Your message has been received. We'll get back to you soon.</p>
          </div>
        )}

        {/* Form */}
        <form className="mm-cf-form" onSubmit={handleSubmit}>
          {/* Issue Type Field */}
          <div className="mm-cf-form-group">
            <label htmlFor="issueType" className="mm-cf-form-label">
              <FiMessageSquare className="mm-cf-label-icon" />
              Type of Issue
            </label>
            <select
              id="issueType"
              name="issueType"
              value={formData.issueType}
              onChange={handleChange}
              className="mm-cf-form-select"
            >
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="feedback">General Feedback</option>
              <option value="suggestion">Suggestion</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Description Field */}
          <div className="mm-cf-form-group">
            <label htmlFor="description" className="mm-cf-form-label">
              <FiMessageSquare className="mm-cf-label-icon" />
              Feedback/Issue Description
            </label>
            <p className="mm-cf-field-hint">Please provide a complete and detailed description</p>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Share your thoughts, report an issue, or provide feedback..."
              className="mm-cf-form-textarea"
              rows="6"
            ></textarea>
          </div>

          {/* <CHANGE> Attachment Field - Creative UI with drag and drop */}
          <div className="mm-cf-attachment-group">
            <label className="mm-cf-form-label">
              <FiPaperclip className="mm-cf-label-icon" />
              Attach Files (Optional)
            </label>
            <p className="mm-cf-field-hint">Attach screenshots or PDFs to help explain your query</p>
            
            <div 
              className="mm-cf-attachment-upload-box"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="attachment"
                ref={fileInputRef}
                className="mm-cf-attachment-input"
                onChange={handleFileChange}
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx"
              />
              
              {selectedFile ? (
                <div className="mm-cf-file-selected">
                  <div className="mm-cf-file-icon-wrapper">
                    <FiPaperclip className="mm-cf-file-icon" />
                  </div>
                  <div className="mm-cf-file-info">
                    <p className="mm-cf-file-name">{selectedFile.name}</p>
                    <p className="mm-cf-file-size">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <button
                    type="button"
                    className="mm-cf-file-remove"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveFile()
                    }}
                    aria-label="Remove file"
                  >
                    <FiX className="mm-cf-remove-icon" />
                  </button>
                </div>
              ) : (
                <div className="mm-cf-upload-placeholder">
                  <FiPaperclip className="mm-cf-placeholder-icon" />
                  <p className="mm-cf-placeholder-text">
                    <span className="mm-cf-placeholder-primary">Click to upload</span> or drag and drop
                  </p>
                  <p className="mm-cf-placeholder-subtext">PDF, PNG, JPG, GIF or DOC up to 10MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" className="mm-cf-submit-button" disabled={isLoading}>
            {isLoading ? "Sending..." : "Send Message"}
          </button>
        </form>

        {/* Footer Note */}
        <p className="mm-cf-form-footer">We appreciate your feedback and will respond as soon as possible.</p>
      </div>
    </div>
  )
}